import {
  LayoutDashboard,
  Users,
  FileText,
  Database,
  ChevronLeft,
  ChevronRight,
  Globe,
  LogOut,
  LayoutTemplate,
  Settings,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUiLocale } from '../../contexts/UiLocaleContext';
import { LanguageFlagByCode, type LanguageCode } from './LanguageFlag';
import { Select, SelectContent, SelectItem, SelectTrigger } from './ui/select';
import type { HawkRole } from '../rolePaths';
import './Sidebar.css';

type SidebarNavItem = { icon: LucideIcon; path: string; labelKey: string };

interface SidebarProps {
  currentRole: HawkRole;
}

type SidebarLanguageCode = LanguageCode;

const SIDEBAR_LANGUAGE_CODES: SidebarLanguageCode[] = ['en', 'de', 'vi', 'fr'];

const NAV_SECTION_KEY: Record<HawkRole, string> = {
  admin: 'nav.section.administration',
  moderator: 'nav.section.editorial',
  subscriber: 'nav.section.yourAccount',
};

function HawkWingLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 40 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M20 24V13.5M20 13.5C14.5 13.5 8.5 9.5 5 4C9.5 7 14 10 20 12M20 13.5C25.5 13.5 31.5 9.5 35 4C30.5 7 26 10 20 12"
        stroke="url(#hawk-wing-stroke)"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
      <path
        d="M20 12C13 10 7.5 6 4 2C8 5.5 13.5 8.5 20 10.5C26.5 8.5 32 5.5 36 2C32.5 6 27 10 20 12Z"
        fill="url(#hawk-wing-fill)"
      />
      <defs>
        <linearGradient id="hawk-wing-fill" x1="4" y1="2" x2="36" y2="14" gradientUnits="userSpaceOnUse">
          <stop stopColor="#38bdf8" />
          <stop offset="1" stopColor="#2563eb" />
        </linearGradient>
        <linearGradient id="hawk-wing-stroke" x1="5" y1="4" x2="35" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#7dd3fc" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function Sidebar({ currentRole }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useUiLocale();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  }, [isMobile]);

  const adminMenuItems: SidebarNavItem[] = [
    { icon: LayoutDashboard, labelKey: 'nav.dashboard', path: '/dashboard' },
    { icon: Users, labelKey: 'nav.moderators', path: '/moderators' },
    { icon: Settings, labelKey: 'nav.settings', path: '/settings' },
    // { icon: Cpu, labelKey: 'nav.aiManager', path: '/ai-manager' },
    // { icon: Mail, labelKey: 'nav.mailOverview', path: '/mail-manager' },
    // { icon: Globe2, labelKey: 'nav.websiteScraper', path: '/website-scraper' },
  ];

  const moderatorMenuItems: SidebarNavItem[] = [
    { icon: LayoutDashboard, labelKey: 'nav.dashboard', path: '/editorial' },
    { icon: Globe, labelKey: 'nav.scraperPool', path: '/scraper-pool' },
    { icon: Database, labelKey: 'nav.dataPool', path: '/collected-raw' },
    { icon: SlidersHorizontal, labelKey: 'nav.dataroomSetting', path: '/dataroom-setting' },
    { icon: Users, labelKey: 'nav.subscribers', path: '/subscribers' },
    { icon: LayoutTemplate, labelKey: 'nav.emailTemplates', path: '/templates' },
    // WIP — re-enable when LinkedIn campaigns are ready for moderators
    // { icon: Megaphone, labelKey: 'nav.linkedin', path: '/linkedin' },
    // { icon: Calendar, labelKey: 'nav.linkedinScheduled', path: '/linkedin-scheduled' },
    // { icon: Inbox, label: 'Drafts / Outbox', path: '/outbox' },
    // { icon: History, label: 'Delivery History', path: '/delivery-history' },
  ];

  const subscriberMenuItems: SidebarNavItem[] = [
    { icon: FileText, labelKey: 'nav.myNewsletters', path: '/newsletters' },
  ];

  const menuItems =
    currentRole === 'admin'
      ? adminMenuItems
      : currentRole === 'moderator'
        ? moderatorMenuItems
        : subscriberMenuItems;

  const displayName = user?.displayName ?? user?.email ?? 'User';
  const initial = (displayName || 'U').trim().charAt(0).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const navSectionLabel = t(NAV_SECTION_KEY[currentRole]);
  const roleLabel = t(`role.${currentRole}`);

  const navStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.05, delayChildren: 0.06 } },
  };

  const navItemMotion = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
    },
  };

  const mobileSidebarWidth = 56;

  return (
    <motion.aside
      data-collapsed={isCollapsed}
      initial={false}
      animate={{ width: isMobile ? mobileSidebarWidth : (isCollapsed ? 72 : 242) }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="hawk-sidebar hawk-sidebar--glass relative shrink-0"
    >
      <div className="hawk-sidebar__decor-grid" aria-hidden />
      <div className="hawk-sidebar__decor-glow" aria-hidden />
      <div className="hawk-sidebar__corner-dots" aria-hidden>
        <span className="hawk-sidebar__corner-dot hawk-sidebar__corner-dot--tl" />
        <span className="hawk-sidebar__corner-dot hawk-sidebar__corner-dot--tr" />
        <span className="hawk-sidebar__corner-dot hawk-sidebar__corner-dot--bl" />
        <span className="hawk-sidebar__corner-dot hawk-sidebar__corner-dot--br" />
      </div>
      <div className="hawk-sidebar__noise pointer-events-none absolute inset-0 z-0" aria-hidden />

      <header className="hawk-sidebar__header">
        <div className="hawk-sidebar__topbar">
          <div className="hawk-sidebar__brand">
            <span className="hawk-sidebar__logo">
              <HawkWingLogo className="hawk-sidebar__logo-svg" />
            </span>
            {!isCollapsed && (
              <h1 className="hawk-sidebar__title">
                <span className="hawk-sidebar__title-text">{t('sidebar.brand').toUpperCase()}</span>
              </h1>
            )}
          </div>
        </div>
      </header>

      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`hawk-sidebar__collapse ${isMobile ? 'hidden' : ''}`}
        aria-label={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
      >
        {isCollapsed ? (
          <ChevronRight className="hawk-sidebar__collapse-icon" strokeWidth={2.25} />
        ) : (
          <ChevronLeft className="hawk-sidebar__collapse-icon" strokeWidth={2.25} />
        )}
      </button>

      <nav className="hawk-sidebar__nav" aria-label="Main navigation">
        {!isCollapsed && <p className="hawk-sidebar__section">{navSectionLabel}</p>}
        <motion.ul
          key={currentRole}
          className="hawk-sidebar__list"
          variants={navStagger}
          initial="hidden"
          animate="visible"
        >
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const label = t(item.labelKey);
            return (
              <motion.li key={item.path} className="hawk-sidebar__item" variants={navItemMotion}>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.012 }}
                  whileTap={{ scale: 0.985 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 24 }}
                  onClick={() => navigate(item.path)}
                  title={isCollapsed ? label : undefined}
                  className={[
                    'hawk-sidebar__link',
                    isActive ? 'hawk-sidebar__link--active' : 'hawk-sidebar__link--idle',
                    isCollapsed && isActive ? 'hawk-sidebar__link--collapsed' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {isActive && (
                    <motion.span
                      layoutId="hawk-sidebar-active-pill"
                      className={[
                        'hawk-sidebar__active-pill',
                        isCollapsed ? 'hawk-sidebar__active-pill--collapsed' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.85 }}
                      aria-hidden
                    />
                  )}
                  <span className="hawk-sidebar__link-content">
                    <item.icon
                      className="hawk-sidebar__link-icon"
                      strokeWidth={isActive ? 2.1 : 1.65}
                      aria-hidden
                    />
                    {!isCollapsed && <span className="hawk-sidebar__link-label">{label}</span>}
                  </span>
                </motion.button>
              </motion.li>
            );
          })}
        </motion.ul>
      </nav>

      <div className="hawk-sidebar__divider" aria-hidden />

      <div className="hawk-sidebar__footer-wrap">
        <div className="hawk-sidebar__footer">
          <div className="hawk-sidebar__footer-inner">
            <div className="hawk-sidebar__footer-user">
              <div className="hawk-sidebar__avatar-wrap">
                <div className="hawk-sidebar__avatar" aria-hidden>
                  {initial}
                </div>
                <span className="hawk-sidebar__avatar-status" aria-hidden />
              </div>
              {!isCollapsed && (
                <div className="hawk-sidebar__user-meta">
                  <p className="hawk-sidebar__user-name">{displayName}</p>
                  <p className="hawk-sidebar__user-role">{roleLabel}</p>
                </div>
              )}
            </div>

            {!isCollapsed ? (
              <div className="hawk-sidebar__footer-controls">
                <Select value={locale} onValueChange={(value) => setLocale(value as SidebarLanguageCode)}>
                  <SelectTrigger aria-label={t('sidebar.language')} className="hawk-sidebar__lang-select">
                    <LanguageFlagByCode code={locale} className="h-3.5 w-3.5" />
                    <span className="hawk-sidebar__lang-label">{t(`locale.${locale}`)}</span>
                  </SelectTrigger>
                  <SelectContent align="end" side="top" className="min-w-[9rem]">
                    {SIDEBAR_LANGUAGE_CODES.map((code) => (
                      <SelectItem key={code} value={code}>
                        <span className="flex items-center gap-2">
                          <LanguageFlagByCode code={code} className="h-3.5 w-3.5" />
                          <span>{t(`locale.${code}`)}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={handleLogout}
                  title={t('sidebar.logOut')}
                  className="hawk-sidebar__icon-btn"
                  aria-label={t('sidebar.logOut')}
                >
                  <LogOut className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            ) : (
              <div className="hawk-sidebar__footer-actions">
                <button
                  type="button"
                  onClick={handleLogout}
                  title={t('sidebar.logOut')}
                  className="hawk-sidebar__icon-btn"
                  aria-label={t('sidebar.logOut')}
                >
                  <LogOut className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.aside>
  );
}
